import { useCallback, useEffect, useState } from 'react';
import * as THREE from 'three';
import { ModelViewerElement } from '@google/model-viewer';
import { ModelScene } from '@google/model-viewer/lib/three-components/ModelScene';

import { ModelViewerItem } from '../useModelViewerEditor/useModelViewerEditor.types';

interface ModelViewerToggleNeutralMaterialProps {
  modelViewerEl: ModelViewerElement;
  activeItem: ModelViewerItem;
}

const traverseScene = (object, callback) => {
  callback(object);
  if (object.children) {
    object.children.forEach((child) => traverseScene(child, callback));
  }
};

let neutralMaterial: THREE.MeshStandardMaterial | null = null;

const createNeutralMaterial = () => {
  if (!neutralMaterial) {
    return (neutralMaterial = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.0,
      roughness: 0.5
    }));
  }

  return neutralMaterial;
};

function useModelViewerToggleNeutralMaterial({
  modelViewerEl,
  activeItem
}: ModelViewerToggleNeutralMaterialProps) {
  const [isNeutralMode, setIsNeutralMode] = useState(false);
  const [originalMaterials, setOriginalMaterials] = useState(new Map());

  useEffect(() => {
    setOriginalMaterials(new Map());
    setIsNeutralMode(false);
  }, [activeItem]);

  const saveOriginalMaterials = useCallback((scene) => {
    const materialsMap = new Map();

    traverseScene(scene, (object) => {
      if (object.material) {
        if (Array.isArray(object.material)) {
          materialsMap.set(object.uuid, [...object.material]);
        } else {
          materialsMap.set(object.uuid, object.material);
        }
      }
    });

    setOriginalMaterials(materialsMap);
  }, []);

  const applyNeutralMaterials = useCallback((scene) => {
    traverseScene(scene, (object) => {
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material = object.material.map(() => createNeutralMaterial());
        } else {
          object.material = createNeutralMaterial();
        }
      }
    });
  }, []);

  const restoreOriginalMaterials = useCallback(
    (scene) => {
      traverseScene(scene, (object) => {
        if (object.material && originalMaterials.has(object.uuid)) {
          object.material = originalMaterials.get(object.uuid);
        }
      });
    },
    [originalMaterials]
  );

  const toggleNeutralMaterial = useCallback(async () => {
    if (!modelViewerEl) return;

    try {
      await modelViewerEl.updateComplete;

      const rootsSymbol = Object.getOwnPropertySymbols(modelViewerEl).find(
        (sym) => sym.toString() === 'Symbol(scene)'
      );

      const scene = modelViewerEl[rootsSymbol] as ModelScene;

      if (!scene) return;

      if (!isNeutralMode) {
        if (originalMaterials.size === 0) {
          saveOriginalMaterials(scene);
        }

        applyNeutralMaterials(scene);
      } else {
        restoreOriginalMaterials(scene);
      }

      scene.queueRender();

      setIsNeutralMode(!isNeutralMode);
    } catch (error) {
      console.error('Error while change materials:', error);
    }
  }, [
    applyNeutralMaterials,
    isNeutralMode,
    modelViewerEl,
    originalMaterials,
    restoreOriginalMaterials,
    saveOriginalMaterials
  ]);

  return {
    isNeutralMode,
    toggleNeutralMaterial
  };
}

export default useModelViewerToggleNeutralMaterial;
